import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { LanguageService } from '@services/language/language.service';
import { ApiErrorMessage, TranslationKeys } from '@tamwely-types/common';
import { MessageService, ToastMessageOptions } from 'primeng/api';

const DEFAULT_LIFE_TIME = 8000;

type Severity = 'info' | 'success' | 'warn' | 'error';
type PublishInternalMessage =
  | {
      summary: TranslationKeys;
      severity: Severity;
      displayType: 'toast';
      params?: Record<string, unknown>;
      life?: number;
    }
  | {
      summary: TranslationKeys;
      severity: Severity;
      displayType: 'message';
      formKey: string;
      params?: Record<string, unknown>;
      life?: number;
    };

type PublishHttpErrorMessage =
  | {
      response: unknown;
      displayType: 'toast';
      life?: number;
    }
  | {
      response: unknown;
      displayType: 'message';
      formKey: string;
      life?: number;
    };

/**
 * @author Abdulrahman Bin Rushud @user arushud>
 */

@Injectable({
  providedIn: 'root',
})
export class AppMessageService {
  private readonly messageService = inject(MessageService);
  private readonly languageService = inject(LanguageService);

  toastChanged = signal<ToastMessageOptions[]>([]);

  handleHttpErrorMessage(messageProp: PublishHttpErrorMessage) {
    if (messageProp.response instanceof HttpErrorResponse) {
      const { status } = messageProp.response;


      const errors = [messageProp.response.error as ApiErrorMessage].map(
        ({ messageAr, messageEn }): ToastMessageOptions => ({
          key: messageProp.displayType,
          life: messageProp.life ?? DEFAULT_LIFE_TIME,
          severity: 'error',
          summary:
            // eslint-disable-next-line no-magic-numbers
            status === 0
              ? this.languageService.getTranslationKey('common.errors.unknownStatus')
              : '',
          data: {
            addedAt: Date.now(),
            error: {
              messageAr,
              messageEn,
            },
          },
        })
      );

      if (messageProp.displayType === 'toast') {
        const now = Date.now();
        const newToastList = this.toastChanged().filter(
          (toast) => now - toast.data.addedAt < toast.life!
        );

        this.messageService.clear('toast');
        this.toastChanged.set([...newToastList, ...errors]);
      } else {
        const withFormKey = errors.map((error) => ({
          ...error,
          data: {
            ...error.data,
            formKey: messageProp.formKey,
          },
        }));
        this.messageService.addAll(withFormKey);
      }
    }
  }

  publishMessage(messageProp: PublishInternalMessage) {
    const message: ToastMessageOptions = {
      key: messageProp.displayType,
      life: messageProp.life ?? DEFAULT_LIFE_TIME,
      severity: messageProp.severity,
      summary: messageProp.summary,
      data: {
        addedAt: Date.now(),
        params: messageProp.params,
      },
    };

    if (messageProp.displayType === 'toast') {
      const now = Date.now();
      const newToastList = this.toastChanged().filter(
        (toast) => now - toast.data.addedAt < toast.life!
      );

      this.messageService.clear('toast');
      this.toastChanged.set([...newToastList, message]);
    } else {
      message.data.formKey = messageProp.formKey;
      this.messageService.add(message);
    }
  }
}
